import { type CountryCode, type PaymentMethod, type ProjectCode } from '@constants'
import { type PaymentMethodType } from '@hooks-api/types/dataModel/payment'
import { type AllProduct, type Variation } from '@hooks-api/types/dataModel/products'

export enum CampaignStatus {
  TESTING = 'testing',
  IN_PROGRESS = 'in_progress',
  PAUSED = 'paused',
  NOT_YET = 'not_yet',
  EXPIRED = 'expired',
}

export interface Brand {
  brand_name: string
  brand_code: string
  brand_url: string
  brand_image: string
  project_code: ProjectCode
  company_name: string
}

export interface Campaign {
  title: string
  campaign_id: number
  language: string
  decimal: number
  status: CampaignStatus
  currency: string
  tax_rate: number
  start_time: string
  end_time: string
  brand_codes_info: Brand[]
  available_regions: CountryCode[]
  search_keywords: string[]
  is_giveaway_dispatch: boolean
  giveaway_thumbnail: string
  payment_methods: {
    payment_method: PaymentMethodType
    payment_setting: {
      priority: number
    }
  }[]
  enable_pickup_giveaway: boolean
  enable_delivery_giveaway: boolean
  enable_giveaway: boolean
  timezone_offset: string
  region: CountryCode
}

export type DeliveryType = 'normal' | 'large' | 'frozen'
interface Delivery {
  delivery_name: string
  rule: number
  fee: number
  delivery_class: DeliveryType
}

export interface Gift {
  display_name: string
  is_out_of_stock: boolean
  project_code: string
  sku: string
  stock: number
  thumbnail: string
}

export interface GiveawayRule {
  accumulable: boolean
  amount: number
  count: number
  display: string
  gifts: Gift[]
  project_code: ProjectCode
  type: 'giveaway'
}

export interface CampaignsProduct {
  products: {
    tab_display_name: string
    bundle_ids: string[]
    all_product: AllProduct[]
  }[]
  bundles: Record<string, {
    gift: string
    discount: number
    fixed_price: number
    display_name: string
    discount_type: 'dis_price' | 'fixed_price' | 'dis_rate'
    criteria_quantity: number
  }[]>
  delivery: Record<DeliveryType, Delivery>
  additions: {
    bundle_ids: string[]
    all_product: AllProduct[]
    tab_display_name: string
  }[]
  gifts: GiveawayRule[]
}

export interface PaymentUrl {
  id: number
  url: string
  total: number
  user_id: number
  created_at: string
  updated_at: string
  firebase_key: string
  payment_method: PaymentMethod
  payment_secret: string
}

type DiscountInfo = CampaignsProduct['gifts'][number] & { type: 'giveaway' | 'bundle_discount' | 'shipping_fee' }

interface AddressMeta {
  address1: string
  address2: string
  last_name: string
  first_name: string
}

interface Shipping {
  id: number
  order_id: number
  sequence_index: number
  status: string
  shipping_date: string
  billing_address_email: string
  billing_address_phone: string
  billing_address_company: string
  billing_address_postcode: string
  billing_address_country: string
  billing_address_state: string
  billing_address_city: string
  billing_address_meta: AddressMeta
  shipping_address_company: string
  shipping_address_postcode: string
  shipping_address_country: string
  shipping_address_state: string
  shipping_address_city: string
  shipping_address_meta: AddressMeta
  created_at: Date
  updated_at: Date
  deleted_at: string
}
interface ChildOrder {
  id: number
  project_code: string
  order_type: string
  order_id: string
  parent_order_id: number
  user_id: number
  country_code: string
  status: FastpassOrderStatus
  currency: string
  customer_id: number
  customer_note: string
  delivery_reason: string
  payment_method: string
  payment_method_title: string
  shipping_method: 'normal' | 'large' | 'frozen'
  shipping_method_title: string
  shipping_warehouse: string
  discount_total: number
  shipping_total: number
  total: number
  created_at: Date
  updated_at: Date
  deleted_at: string
  order_items: (Variation & { quantity: number })[]
  shipping: Shipping
}

/**
 * @deprecated
 */
export interface FastpassOrder {
  id: string
  campaign_id: string
  order_type: string
  order_hash: string
  user_id: number
  country_code: string
  status: FastpassOrderStatus
  customer_note: string
  payment_method: string
  delivery_date: string
  payment_secret: string
  item_confirmed: number
  additional_amount: number
  total: number
  discount_total: number
  shipping_total: number
  child_orders: ChildOrder[]
  discount_info: DiscountInfo[]
  is_giveaway_dispatch: boolean
  tax: number
}

/**
 * @deprecated
 */
export enum FastpassOrderStatus {
  UNCREATED = 'uncreated', // 尚未成立
  PENDING = 'pending', // 尚未付款
  PROCESSING = 'processing', // 處理中
  COMPLETED = 'completed', // 已完成
  CANCELLED = 'cancelled', // 已取消
}

export interface PaymentAppKey {
  app_id: string
  app_key: string
}

export interface PaymentCheckout {
  campaign_code: string
  parent_order_id: string
  payment_method: PaymentMethod.CHECKOUTPAY_DIRECT | PaymentMethod.TAPPAY_DIRECT
  prime: string
  receipt_type: string
  phone_carrier_number: string
  business_ubn_title: string
  business_ubn: string
  refund_statement: boolean
  payment_url: string
}

export interface AdminLogin {
  name?: string
  token?: string
  user_id?: string
}
